/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { ROUTES } from '../../constants/routes';
import { generateUrl } from '../../routes/util';
import { CreateCustomReportType, CustomReport, getMetricTypeLabel, MetricType } from '../../types/custom-report';
import { CustomReportRoutes } from './CustomReportRoutes';
import './styles.scss';

interface CustomReportsBreadcrumbsProps {
  customReport?: CustomReport;
}

interface UrlParams {
  initiativeId?: string;
  reportId?: string;
  action?: 'add';
  metricType?: MetricType;
}

const filterParams = (params: UrlParams, filter: (keyof UrlParams)[]) => ({
  initiativeId: params.initiativeId,
  reportId: filter.includes('reportId') ? params.reportId : undefined,
  action: filter.includes('action') ? params.action : undefined,
  metricType: filter.includes('metricType') ? params.metricType : undefined,
});

function getCreateType(type: CreateCustomReportType) {
  switch (type) {
    case CreateCustomReportType.Subsidiary:
      return 'Subsidiary comparison report builder';
    case CreateCustomReportType.Date:
      return 'Date comparison report builder';
    case CreateCustomReportType.Calculated:
      return 'Calculated report builder';
    case CreateCustomReportType.Template:
      return 'Custom report builder';
  }
}

const CustomReportsBreadcrumbs = (props: CustomReportsBreadcrumbsProps) => {
  const { customReport } = props;

  const history = useHistory();
  const route = useRouteMatch<UrlParams>();
  const params = route.params;

  const breadcrumbs = [
    {
      label: 'Downloads',
      href: generateUrl(ROUTES.DOWNLOADS, filterParams(params, []))
    },
    {
      label: 'Custom reports',
      href: generateUrl(CustomReportRoutes.ROOT, filterParams(params, []))
    }
  ];

  const createRoute = useRouteMatch<{ createType: CreateCustomReportType }>(CustomReportRoutes.CREATE_REPORT);
  if (createRoute) {
    breadcrumbs.push({
      label: 'Report selection',
      href: generateUrl(CustomReportRoutes.CREATE_REPORT, filterParams(params, []))
    });
    if (createRoute.params.createType) {
      breadcrumbs.push({
        label: getCreateType(createRoute.params.createType),
        href: generateUrl(CustomReportRoutes.CREATE_REPORT, filterParams(params, []))
      });
    }
  } else if (customReport) {
    breadcrumbs.push({
      label: customReport.name,
      href: generateUrl(CustomReportRoutes.MANAGE_REPORT, filterParams(params, ['reportId']))
    });
    if (params.action === 'add') {
      breadcrumbs.push({
        label: 'Metric type',
        href: generateUrl(CustomReportRoutes.ADD_METRIC, filterParams(params, ['reportId']))
      })
      if (params.metricType) {
        breadcrumbs.push({
          label: getMetricTypeLabel(params.metricType),
          href: generateUrl(CustomReportRoutes.ADD_METRIC, filterParams(params, ['reportId', 'metricType']))
        })
      }
    }
  }

  return (
    <Breadcrumb>
      {breadcrumbs.map((breadcrumb, i) => {
        const isActive = i === breadcrumbs.length - 1;
        return (
          <BreadcrumbItem
            key={`crb-${i}-${breadcrumb.href}`}
            tag={isActive ? 'span' : 'a'}
            active={isActive}
            href={isActive ? undefined : breadcrumb.href}
            onClick={isActive ? undefined : (e) => {
              e.preventDefault();
              history.push(breadcrumb.href);
            }}
          >{breadcrumb.label}</BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
}

export default CustomReportsBreadcrumbs;
